import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useHR } from '../HRContext';
import { errorText } from '../../utils';
import { actionLabel, download, fmtValue, titleCase, toCSV } from '../format';
import Modal from './Modal';
import RecordForm, { uploadPickedFiles } from './Form';
import StatusPill from './StatusPill';
import Loader from './Loader';
import { Select } from './Controls';

// Register over one resource (see src/hr/resources.js) — the port of Stephanet's EntityView.
// Lists, searches, filters, exports; a row opens its detail with workflow moves, extra actions, edit and delete.

const NUMERIC = ['money', 'number', 'integer'];
const DANGER = ['rejected', 'cancelled', 'suspended', 'exited', 'revoked'];

const cell = (f, r) => (f.pill ? <StatusPill value={f.get ? f.get(r) : r[f.name]} /> : f.render ? f.render(r) : fmtValue(f, r));

// canEdit: who may change the records, when it is not the HR managers (e.g. a Payroll permission)
export function RecordDetail({ res, record, onClose, onChanged, canEdit: canEditAll }) {
  const { canManage: hrManage, notify, confirm } = useHR();
  const canManage = canEditAll ?? hrManage;
  const [editing, setEditing] = useState(false);
  const [acting, setActing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const status = res.statusField ? record[res.statusField] : null;
  const next = canManage && res.workflow ? (res.workflow[status] || []) : [];
  const extra = (res.actions?.(record, { canManage }) || []).filter(Boolean);
  const link = res.detailLink?.(record);
  const fields = res.fields.filter(f => f.detail !== false);

  const run = async (fn, done) => {
    setBusy(true); setError('');
    try { await fn(); notify(done); onChanged(); } catch (err) { setError(errorText(err)); }
    setBusy(false);
  };
  const move = async to => {
    if (DANGER.includes(to) && !(await confirm({ title: actionLabel(to), message: `${actionLabel(to)} this ${res.label.toLowerCase()}?`, confirmLabel: actionLabel(to), danger: true }))) return;
    run(() => res.transition(record, to), `${res.label} moved to ${titleCase(to)}`);
  };
  const remove = async () => {
    if (!(await confirm({ title: `Delete ${res.label}`, message: `Delete this ${res.label.toLowerCase()}?`, detail: 'This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return;
    run(() => res.remove(record), `${res.label} deleted`).then(onClose);
  };

  // an action with fields asks for them first (e.g. hiring an applicant needs a hire date)
  if (acting) {
    return (
      <RecordForm title={`${acting.label}: ${res.title ? res.title(record) : res.label}`} fields={acting.fields} initial={acting.initial?.(record)}
                  submitLabel={acting.label} onClose={() => setActing(null)}
                  onSubmit={async (payload, data) => { await acting.run(record, payload, data); notify(acting.done || `${acting.label} done`); setActing(null); onChanged(); }} />
    );
  }

  if (editing) {
    const formFields = res.editFields || res.formFields || res.fields.filter(f => f.form !== false && f.form !== 'create');
    return (
      <RecordForm title={`Edit ${res.label}`} fields={formFields} initial={record} onClose={() => setEditing(false)}
                  onSubmit={async (payload, data) => { const saved = await res.update(record, payload); await uploadPickedFiles(formFields, data, { ...record, ...(saved || {}) }); notify(`${res.label} updated`); setEditing(false); onChanged(); }} />
    );
  }

  const canEdit = canManage && res.update && (!res.canUpdate || res.canUpdate(record));
  const canDelete = canManage && res.remove && (!res.canRemove || res.canRemove(record));
  return (
    <Modal title={`${res.label}: ${res.title ? res.title(record) : `#${record.id}`}`} onClose={onClose} wide
           footer={(canEdit || canDelete || next.length > 0 || extra.length > 0) && (
             <>
               {canDelete && <button className="btn danger ghost" disabled={busy} onClick={remove}><Trash2 size={15} /> Delete</button>}
               <div className="spacer" />
               {extra.map(a => (
                 <button key={a.label} className={`btn ${a.tone === 'danger' ? 'danger ghost' : a.tone === 'primary' ? 'primary' : ''}`} disabled={busy}
                         onClick={async () => {
                           if (a.fields) return setActing(a);
                           if (a.confirm && !(await confirm({ title: a.label, message: a.confirm, confirmLabel: a.label, danger: a.tone === 'danger' }))) return null;
                           return run(() => a.run(record), a.done || `${a.label} done`);
                         }}>
                   {a.label}
                 </button>
               ))}
               {next.map(to => (
                 <button key={to} className={`btn ${DANGER.includes(to) ? 'danger' : 'primary'}`} disabled={busy} onClick={() => move(to)}>{actionLabel(to)}</button>
               ))}
               {canEdit && <button className="btn" onClick={() => setEditing(true)}><Pencil size={15} /> Edit</button>}
             </>
           )}>
      {link && <Link className="btn ghost small" to={link.to}>{link.label} →</Link>}
      <dl className="detail-grid">
        {fields.map(f => (
          <div key={f.name}>
            <dt>{f.label}</dt>
            <dd>{cell(f, record)}</dd>
          </div>
        ))}
      </dl>
      {res.renderDetail?.(record)}
      {error && <div className="error">{error}</div>}
    </Modal>
  );
}

export default function Register({ res, filter = {}, title, compact, defaults, canEdit }) {
  const { canManage: hrManage, notify } = useHR();
  const canManage = canEdit ?? hrManage;
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const size = compact ? 10 : 25;
  const filterKey = JSON.stringify(filter);

  const load = useCallback(async () => {
    setError('');
    try { setRows(await res.load(filter)); } catch (err) { setError(errorText(err)); setRows([]); }
  }, [res, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setRows(null); load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, status, filterKey]);

  const columns = useMemo(() => res.fields.filter(f => f.list).slice(0, compact ? 6 : 10), [res, compact]);
  const statusOptions = res.statusField && res.fields.find(f => f.name === res.statusField)?.options;

  const shown = useMemo(() => (rows || []).filter(r => {
    if (status && String(r[res.statusField]) !== status) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (res.search || columns.map(c => c.name)).some(n => String((typeof n === 'function' ? n(r) : r[n]) ?? '').toLowerCase().includes(t));
  }), [rows, q, status, res, columns]);

  if (res.notConnected) {
    return <div className="panel"><div className="alert info">{res.notConnected}</div></div>;
  }

  const pages = Math.max(1, Math.ceil(shown.length / size));
  const pageRows = shown.slice((page - 1) * size, page * size);
  const canCreate = canManage && res.create;

  const exportCSV = () => download(`${res.key}.csv`, toCSV(
    res.fields.filter(f => f.detail !== false).map(f => ({ label: f.label, get: r => (f.pill ? titleCase(r[f.name]) : fmtValue(f, r)) })), shown));

  const createFields = res.createFields || res.formFields || res.fields.filter(f => f.form !== false && f.form !== 'edit');

  return (
    <div className="panel">
      <div className="toolbar">
        {title && <h3 className="panel-title">{title}</h3>}
        <div className="search">
          <Search size={16} />
          <input placeholder={`Search ${(res.plural || `${res.label}s`).toLowerCase()}…`} value={q} onChange={e => setQ(e.target.value)} />
        </div>
        {statusOptions && !filter[res.statusField] && (
          <Select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All Status</option>
            {statusOptions.map(o => (typeof o === 'object' ? <option key={o.value} value={o.value}>{o.label}</option> : <option key={o} value={o}>{titleCase(o)}</option>))}
          </Select>
        )}
        <div className="spacer" />
        <span className="muted small">{shown.length} records</span>
        <button className="icon-btn" title="Refresh" onClick={load}><RefreshCw size={16} /></button>
        {!compact && <button className="btn ghost" onClick={exportCSV} disabled={!shown.length}><Download size={15} /> Export</button>}
        {canCreate && <button className="btn primary" onClick={() => setCreating(true)}><Plus size={15} /> New {res.label}</button>}
      </div>
      {error && <div className="error">{error}</div>}
      <div className={`table-wrap ${compact ? '' : 'register-wrap'}`}>
        <table className="register-table">
          <thead>
            <tr>{columns.map(c => <th key={c.name} className={NUMERIC.includes(c.type) ? 'num' : ''}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {pageRows.map(r => (
              <tr key={r.id} onClick={() => setSelected(r)} className="clickable">
                {columns.map(c => <td key={c.name} className={NUMERIC.includes(c.type) ? 'num' : ''}>{cell(c, r)}</td>)}
              </tr>
            ))}
            {rows === null && <tr><td colSpan={columns.length}><Loader title={`Loading ${res.label.toLowerCase()} records…`} /></td></tr>}
            {rows !== null && shown.length === 0 && (
              <tr><td colSpan={columns.length} className="empty">
                {rows.length ? 'No records match these filters.' : `No records yet${canCreate ? ' — click "New" to add one.' : '.'}`}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="pager">
          <button className="btn ghost small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {page} of {pages}</span>
          <button className="btn ghost small" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
      {creating && (
        <RecordForm title={`New ${res.label}`} fields={createFields} initial={{ ...(res.defaults || {}), ...filter, ...(defaults || {}) }}
                    onClose={() => setCreating(false)}
                    onSubmit={async (payload, data) => {
                      const created = await res.create({ ...filter, ...payload });
                      // files go up once the record exists; if one is refused the record stays and the reason is shown
                      try { await uploadPickedFiles(createFields, data, created); notify(`${res.label} created`); }
                      catch (err) { notify(`${res.label} created, but the file was not uploaded: ${errorText(err)}`, 'bad'); }
                      setCreating(false); load();
                    }} />
      )}
      {selected && (
        <RecordDetail res={res} record={selected} canEdit={canEdit} onClose={() => setSelected(null)}
                      onChanged={async () => { const fresh = await res.load(filter).catch(() => null); if (fresh) { setRows(fresh); setSelected(fresh.find(x => x.id === selected.id) || null); } }} />
      )}
    </div>
  );
}
